import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Unsubscribable } from 'rxjs';

import { CirclePacking, CirclePackingCanvasComponent } from '@innovation-os-ng/ui/chart-ng';
import { CompoundFilter, findLeafByOn, LeafFilter } from '@itonics/filters';

import { CommonModule } from '@angular/common';
import { HotspotModule } from '@innovation-os-ng/ui/hotspot';
import { LoaderModule } from '@innovation-os-ng/ui/loader';
import { Ui2ButtonModule } from '@innovation-os-ng/ui/ui2-button';
import { TitleCasePipe } from '@innovation-os-ng/ui/utils';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import { CATEGORY_PROPERTY } from '../../../shared/constants/constants';
import { TrackedActions, TrackedEvents } from '../../../shared/constants/tracker-ids';
import { filterHierarchical } from '../../../shared/helpers/filter-hierarchical';
import { flatHierarchical } from '../../../shared/helpers/flat-hierarchical';
import { getNode } from '../../../shared/helpers/get-node';
import { FilterChangeAction, SignalsFilters, SignalsType } from '../../../shared/model/types';
import { AggregateTopicsService } from '../../../shared/modules/shared-services/services/aggregate-topics.service';
import { InsightsTrackingService } from '../../../shared/modules/shared-services/services/insights-tracking-service';
import { MetricService } from '../../../shared/modules/shared-services/services/metric.service';

/**
 * Cluster visualization component for Categories
 */

const VISUALIZATION_TOPICS = 'signals-ng-visualization-tab.visualizationViews.topics';
const RESET_VISUALIZATION = 'signals-ng-visualization-tab.clusterVisualization.reset';
const UNCATEGORIZED_SIGNALS = 'signals-ng-visualization-tab.visualizationViews.uncategorizedSignals';

@Component({
  selector: 'signals-circle-packing',
  templateUrl: './circle-packing.component.html',
  styleUrls: ['./circle-packing.component.scss'],
  standalone: true,
  imports: [CommonModule, LoaderModule, Ui2ButtonModule, TranslateModule, TitleCasePipe, CirclePackingCanvasComponent, HotspotModule]
})
export class CirclePackingComponent implements OnInit, OnDestroy {
  private documentsFilter: CompoundFilter;
  private documentsFilterChanges: Unsubscribable;

  @Input()
  set filter(filter: CompoundFilter) {
    if (!filter) {
      return;
    }
    this.unsubscribe();
    this.documentsFilter = filter;
    this.subscribe();
    this.getAggregateData();
  }

  @Output()
  filterChanged: EventEmitter<{
    action: FilterChangeAction;
    filter: LeafFilter;
  }> = new EventEmitter();

  @Output()
  loadingChanged: EventEmitter<boolean> = new EventEmitter();

  /*
   * DATA STATES
   */

  public noData: boolean;
  private lastRequestTime: number;
  public get loading(): boolean {
    return Boolean(this.lastRequestTime);
  }

  /**
   *  DATA & MODEL
   */
  public data: CirclePacking;
  public fullDataBackup: CirclePacking;

  public insightsType = SignalsType;

  // Show reset data
  public showResetData: boolean;

  private metricChangeSubs: Unsubscribable;

  //Translation variables
  public uncategorizedSignals: string;
  public visualizationTopics: string;
  public resetVisualization: string;

  constructor(
    private readonly aggregateTopicsService: AggregateTopicsService,
    private readonly insightsTrackingService: InsightsTrackingService,
    private readonly metricService: MetricService
  ) {}

  ngOnInit() {
    /*

      We subscribe to changes in the global metric
      to re-fetch data
    */
    this.metricChangeSubs = this.metricService.onMetricChange(() => {
      this.getAggregateData();
    });

    this.visualizationTopics = VISUALIZATION_TOPICS;
    this.uncategorizedSignals = UNCATEGORIZED_SIGNALS;
    this.resetVisualization = RESET_VISUALIZATION;
  }

  ngOnDestroy() {
    this.unsubscribe();
    this.metricChangeSubs.unsubscribe();
  }

  private unsubscribe() {
    if (this.documentsFilterChanges) {
      this.documentsFilterChanges.unsubscribe();
    }
  }

  private subscribe() {
    this.documentsFilterChanges = this.documentsFilter.onChanged((_) => {
      this.getAggregateData();
    });
  }

  private getAggregateData() {
    // The filters that we want for
    // Mark as loading with timestamp
    const timestamp = Date.now();
    this.lastRequestTime = timestamp;
    this.loadingChanged.emit(this.loading);

    this.aggregateTopicsService
      .getAggregateTopics(this.documentsFilter, this.metricService.getGlobalMetric(), 'topics-base')
      .then((res) => {
        if (res.topics && res.topics.length) {
          // We receive from BE all the categories, including the
          // excluded ones in the filter so we need to manually remove
          // them from the aggregate data

          const categoriesFilter = this.getCategoryQuery();
          this.showResetData = !!categoriesFilter;

          this.data = this.filterCategories(
            {
              id: 'root',
              label: 'topics',
              children: res.topics
            },
            categoriesFilter
          );
        } else {
          this.data = null;
        }

        // Only end loading if this was the last request sent
        if (this.lastRequestTime === timestamp) {
          this.lastRequestTime = null;
        }
        this.loadingChanged.emit(this.loading);
        this.noData = !res.topics || !res.topics.length;

        this.fullDataBackup = { id: 'root', label: 'topics', children: [...res.topics] };
      });
  }

  public select(categories: Array<string>) {
    const filter = new LeafFilter(SignalsType.CATEGORY);
    if (categories && categories.length) {
      filter.setValues(categories);
    }

    this.unsubscribe();

    // to avoid calling our own callback
    this.filterChanged.next({
      action: categories && categories.length ? FilterChangeAction.ADD : FilterChangeAction.REMOVE,
      filter
    });
    if (categories && categories.length) {
      this.insightsTrackingService.trackEvent('l6JjnBLn8v1msDW');
    } else {
      this.insightsTrackingService.trackEvent('mufuFEojW5dxOVu');
    }
    this.subscribe();
  }

  /**
   * Track a generic 'visualization' event (Cluster Viz only reference in dimension4)
   *
   * @param name Event being tracked
   */
  public trackEvent(name: string) {
    this.insightsTrackingService.trackEvent(`${TrackedEvents.PREFIX_VIZ}.${name}`, { dimension4: 'Cluster Visualization' });
  }

  public deselect(category: CirclePacking) {
    const categoriesQuery = this.getCategoryQuery();
    const exclusion = categoriesQuery?.getExclude();
    // We create a new LeafFilter
    const filter = new LeafFilter(SignalsType.CATEGORY);
    this.trackEvent(TrackedActions.VALUE_DESELECTED);

    /*

      We when deselect we need to build the values for the filter depending on
      what that filter is currently doing, excluding, including or there is no filter

    */

    if (!categoriesQuery) {
      /*

        If there are no filter for categories
        means that we want to show everything
        in this case after deselection we exclude what needs to be deselected

        We do:
          a. extract the node from the data
          b. collect all nodes in a flat list (this will include all the descendants
             and the node deselected as well)

      */

      const categoryNode = getNode(this.data, category[CATEGORY_PROPERTY], CATEGORY_PROPERTY);
      const newValues = flatHierarchical(categoryNode).map((d) => d[CATEGORY_PROPERTY]);

      filter.setValues(newValues);
      filter.setExclude(true);
    } else if (exclusion) {
      /*

        If there is a category filter AND is an exclusion
        we can keep adding more exclusions

        We do:
          a. extract the node from the data
          b. collect all the nodes in a flat list (this will include all the descendants
             and the node deselected as well)
          c. we combine the previous values from the filter and the flat list

      */

      const categoryNode = getNode(this.data, category[CATEGORY_PROPERTY], CATEGORY_PROPERTY);
      const flatListNodes = flatHierarchical(categoryNode).map((d) => d[CATEGORY_PROPERTY]);
      const newValues = categoriesQuery.getValues().concat(flatListNodes);

      filter.setValues(newValues);
      filter.setExclude(true);
    } else if (categoriesQuery && !exclusion) {
      /*

        If there is a category filter AND is NOT an exclusion
        we need to remove the selected category from the values
        including descendants

      */

      const categoryNode = getNode(this.data, category[CATEGORY_PROPERTY], CATEGORY_PROPERTY);
      const flatListNodes = flatHierarchical(categoryNode).map((d) => d[CATEGORY_PROPERTY]);
      // Extract the list of node from the actual values
      const newValues = categoriesQuery.getValues().filter((value) => flatListNodes.indexOf(value) === -1);

      filter.setValues(newValues);
      filter.setExclude(false);
    }

    // Unsubscribe to avoid refreshing after our own changes
    this.unsubscribe();

    // If there are no values left in the filter, remove it
    const removeFilter = !filter.getValues().length;

    // We change the filter that will update the cluster
    this.filterChanged.next({
      action: removeFilter ? FilterChangeAction.REMOVE : FilterChangeAction.ADD,
      filter
    });

    // Update the rendered data. If we removed the filter, reset to the base data
    this.data = removeFilter ? this.filterCategories(this.fullDataBackup, null) : this.filterCategories(this.data, filter);
    this.showResetData = !removeFilter;

    // Re-subscribe to filter changes
    this.subscribe();
  }

  private getCategoryQuery(): LeafFilter {
    const filterSelection = this.documentsFilter.getQuery(SignalsFilters.SELECTION) as CompoundFilter;

    /*
      we have in filter Category Queries as a Leaf or wrapped in a compound
      so we look for both
    */
    const categoryQuery = filterSelection?.findQuery(findLeafByOn(SignalsType.CATEGORY)) as LeafFilter;
    const categoryFilter = filterSelection?.getQuery(SignalsType.CATEGORY) as CompoundFilter;

    return categoryQuery || (categoryFilter?.getQueries().find((query: LeafFilter) => query.getValues()) as LeafFilter);
  }

  private filterCategories(data: CirclePacking, categoriesQuery: LeafFilter) {
    if (!categoriesQuery) {
      /*
        there are not filters we return the same data
       */
      return data;
    }

    /*

      This filterHierarchical function will give us the required nodes, not including descendants and
      depending on the "categoriesQuery.getExclude()"
        1. TRUE it will apply a subtraction of the "categoriesQuery.getValues()"
        2. FALSE it will extract the values from the data
     */
    return filterHierarchical(data, categoriesQuery.getValues() as Array<string>, CATEGORY_PROPERTY, categoriesQuery.getExclude());
  }

  public resetData() {
    // On reset data we remove categories filter
    const filter = this.getCategoryQuery();

    if (!filter) {
      return;
    }

    this.filterChanged.next({
      action: FilterChangeAction.REMOVE,
      filter
    });
  }
}
